import apiClient from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import type { MasterImpersonateTenantResult } from '@easyflow/shared';

interface ExitImpersonateData {
  token: string;
}

export const impersonationService = {
  async impersonate(tenantId: string) {
    const response = await apiClient.post<MasterImpersonateTenantResult>(`/master/dashboard/impersonate-tenant/${tenantId}`);

    if (response.success && response.data?.token) {
      const tokenData = response.data;
      
      const originalToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
      if (originalToken) {
        localStorage.setItem(AUTH_STORAGE_KEYS.BACKUP_TOKEN, originalToken);
      }
      
      localStorage.setItem(AUTH_STORAGE_KEYS.IMPERSONATION_ACTIVE, 'true');
      
      if (tokenData.impersonated_user) {
        localStorage.setItem(AUTH_STORAGE_KEYS.IMPERSONATED_TENANT, JSON.stringify({
          id: tenantId,
          slug: tokenData.impersonated_user.tenant_slug,
          name: tokenData.impersonated_user.email
        }));
      }
      
      apiClient.startImpersonation(tokenData.token);
    }
    
    return response;
  },

  async exitImpersonate() {
    try {
      const response = await apiClient.post<ExitImpersonateData>('/master/exit-impersonate', {});
      
      apiClient.exitImpersonation();
      
      return response;
    } catch (err: unknown) {
      const backupToken = localStorage.getItem(AUTH_STORAGE_KEYS.BACKUP_TOKEN);
      if (backupToken) {
        localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, backupToken);
        localStorage.removeItem(AUTH_STORAGE_KEYS.BACKUP_TOKEN);
        localStorage.removeItem(AUTH_STORAGE_KEYS.IMPERSONATION_ACTIVE);
        localStorage.removeItem(AUTH_STORAGE_KEYS.IMPERSONATED_TENANT);
        
        return {
          success: true,
          data: { token: backupToken },
          message: 'Restored original session from backup'
        };
      }
      
      throw new Error('Exit impersonation failed and cannot restore original session');
    }
  },

  isImpersonating(): boolean {
    return localStorage.getItem(AUTH_STORAGE_KEYS.IMPERSONATION_ACTIVE) === 'true';
  },

  getImpersonatedTenant(): { id: string; name: string; slug: string } | null {
    const tenantData = localStorage.getItem(AUTH_STORAGE_KEYS.IMPERSONATED_TENANT);
    if (tenantData) {
      try {
        return JSON.parse(tenantData);
      } catch {
        return null;
      }
    }
    return null;
  },

  clearImpersonation(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.IMPERSONATION_ACTIVE);
    localStorage.removeItem(AUTH_STORAGE_KEYS.IMPERSONATED_TENANT);
    localStorage.removeItem(AUTH_STORAGE_KEYS.BACKUP_TOKEN);
  }
};
